<template>
  <AuthFlowShell>
    <AuthFlowCard :show-terms="step === 1" :terms-query="{ redirect: '/login' }">
      <v-fade-transition mode="out-in">
        <RoleSelectionComponent
          v-if="step === 0"
          key="role"
          @role-selected="handleRoleSelection"
        />

        <UserLoginComponent
          v-else-if="step === 1"
          key="login"
          :error-msg="loginErrorMsg"
          :loading="isLoading"
          @logon-details="handleLogin"
          @forgot-password="step = 3"
          @back="goBackToRoleSelection"
        />

        <OTPVerificationComponent
          v-else-if="step === 2"
          key="otp"
          :email="email"
          :error-msg="otpErrorMsg"
          :loading="isLoading"
          :can-resend="canResendOtp"
          :resend-label="otpResendLabel"
          @verify-otp="handleVerifyOTP"
          @back="onOtpBack"
          @resend="handleResendOtp"
        />

        <ForgotPasswordComponent
          v-else-if="step === 3"
          key="forgot-password"
          @back="step = 1"
        />
      </v-fade-transition>
    </AuthFlowCard>
  </AuthFlowShell>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import AuthFlowCard from '~/components/auth/AuthFlowCard.vue'
import AuthFlowShell from '~/components/auth/AuthFlowShell.vue'
import RoleSelectionComponent from '~/components/auth/RoleSelectionComponent.vue'
import UserLoginComponent from '~/components/auth/UserLoginComponent.vue'
import OTPVerificationComponent from '~/components/auth/OTPVerificationComponent.vue'
import ForgotPasswordComponent from '~/components/auth/ForgotPasswordComponent.vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '~/stores/useAuthStore'
import { useGlobalStore } from '~/stores/useGlobalStore'

definePageMeta({ layout: false })

const authStore = useAuthStore()
const globalStore = useGlobalStore()
const { isAuthenticated } = storeToRefs(globalStore)

const {
  canResend: canResendOtp,
  resendLabel: otpResendLabel,
  startCooldown: startOtpResendCooldown,
  resetCooldown: resetOtpResendCooldown,
} = useOtpResendCooldown()

const step = ref(0)
const selectedRole = ref<number | null>(null)
const email = ref('')
const password = ref('')
const isLoading = ref(false)
const loginErrorMsg = ref('')
const otpErrorMsg = ref('')

onBeforeMount(() => {
  if (isAuthenticated.value) {
    navigateTo('/dashboard')
  }
})

watch(step, (currentStep, previousStep) => {
  if (currentStep === 2) {
    startOtpResendCooldown()
  } else if (previousStep === 2) {
    resetOtpResendCooldown()
  }
})

const handleRoleSelection = (role: number) => {
  selectedRole.value = role
  step.value = 1
}

const goBackToRoleSelection = () => {
  loginErrorMsg.value = ''
  step.value = 0
}

const onOtpBack = () => {
  otpErrorMsg.value = ''
  step.value = 1
}

const handleLogin = async (credentials: { email: string; password: string }) => {
  loginErrorMsg.value = ''
  isLoading.value = true

  email.value = credentials.email
  password.value = credentials.password

  const result = await authStore.getOTP(email.value, password.value, selectedRole.value as number)
  if (result.success) {
    step.value = 2
  } else {
    loginErrorMsg.value = result.message || 'Invalid credentials. Please try again.'
  }
  isLoading.value = false
}

const handleResendOtp = async () => {
  if (!canResendOtp.value || isLoading.value) {
    return
  }

  otpErrorMsg.value = ''
  isLoading.value = true

  const result = await authStore.getOTP(email.value, password.value, selectedRole.value as number)

  if (result.success) {
    startOtpResendCooldown()
  } else {
    otpErrorMsg.value = result.message || 'Unable to resend OTP. Please try again.'
  }

  isLoading.value = false
}

const handleVerifyOTP = async (otpValue: string) => {
  otpErrorMsg.value = ''
  isLoading.value = true

  const result = await authStore.verifyOTP(email.value, password.value, otpValue)

  if (result.success) {
    const data = result?.data ?? {}
    globalStore.updateUser(data)
    globalStore.updateRole(data?.role)
    globalStore.updateToken(data?.access ?? '', data?.refresh ?? '')
    await navigateTo('/dashboard')
    return
  }

  otpErrorMsg.value = result.message || 'Invalid OTP. Please try again.'
  isLoading.value = false
}
</script>
